import { useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { currentProjectAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';
import { wsManager } from '@/lib/websocket';
import { useQueryClient } from '@tanstack/react-query';
import { AIPromptOverlay } from './AIPromptOverlay';

export default function PreviewPane() {
  const [currentProject] = useAtom(currentProjectAtom);
  const [isRunning, setIsRunning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [installProgress, setInstallProgress] = useState<{stage: string; message: string; progress: number} | null>(null);
  const hasStartedRef = useRef<string | null>(null);
  const [showAIOverlay, setShowAIOverlay] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (currentProject) {
      // Only auto-start if we haven't started this project yet
      if (hasStartedRef.current !== currentProject.id) {
        hasStartedRef.current = currentProject.id;
        checkAndStartDevServer();
      }
    } else {
      // Reset state when no project is selected
      setIsRunning(false);
      setPreviewUrl(null);
      setIsStarting(false);
      hasStartedRef.current = null;
    }
  }, [currentProject]);

  // Listen for database changes and auto-refresh preview + invalidate cache
  useEffect(() => {
    if (!currentProject) return;

    const setupDatabaseListener = async () => {
      try {
        const socket = await wsManager.connect();

        const handleDatabaseChange = (data: { 
          projectId: string; 
          table: string; 
          operation: string;
          cacheKeys?: string[][];
        }) => {
          if (data.projectId === currentProject.id) {
            console.log(`[PreviewPane] Database changed for project ${currentProject.id}`, data);
            
            // Invalidate React Query cache based on changed table
            if (data.cacheKeys && data.cacheKeys.length > 0) {
              data.cacheKeys.forEach((queryKey) => {
                console.log(`[PreviewPane] Invalidating cache:`, queryKey);
                queryClient.invalidateQueries({ queryKey });
              });
            } else {
              // Fallback: invalidate all queries for common CMS tables
              const tableCacheMap: Record<string, string[][]> = {
                'cms_content': [['cms_content']],
                'cms_questions': [['cms_questions']],
                'cms_scoring': [['cms_scoring']],
                'cms_config': [['cms_config']],
              };
              
              const cacheKeys = tableCacheMap[data.table] || [];
              cacheKeys.forEach((queryKey) => {
                console.log(`[PreviewPane] Invalidating cache (fallback):`, queryKey);
                queryClient.invalidateQueries({ queryKey });
              });
            }
            
            // Refresh preview iframe after cache invalidation
            setTimeout(() => {
              refreshPreview();
            }, 500);
          }
        };

        socket.on('database:changed', handleDatabaseChange);

        // Listen for installation progress
        const handleInstallProgress = (data: {
          projectId: string;
          stage: string;
          message: string;
          progress?: number;
        }) => {
          if (data.projectId === currentProject.id) {
            console.log('[PreviewPane] Install progress:', data);
            setInstallProgress({
              stage: data.stage,
              message: data.message,
              progress: data.progress || 0
            });
            
            // Clear progress when complete
            if (data.stage === 'complete') {
              setTimeout(() => setInstallProgress(null), 2000);
            }
          }
        };

        socket.on('project:install:progress', handleInstallProgress);

        // Cleanup
        return () => {
          socket.off('database:changed', handleDatabaseChange);
        };
      } catch (error) {
        console.error('[PreviewPane] Failed to setup database listener:', error);
      }
    };

    setupDatabaseListener();
  }, [currentProject?.id, queryClient]);

  // Show AI overlay for new projects
  useEffect(() => {
    if (currentProject && isRunning) {
      // Check if this is a newly created project (created in last 5 minutes)
      const createdAt = new Date(currentProject.created_at);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

      if (createdAt > fiveMinutesAgo) {
        setShowAIOverlay(true);
      }
    }
  }, [currentProject, isRunning]);

  const checkAndStartDevServer = async () => {
    if (!currentProject) return;

    try {
      // Check current status
      const response = await apiRequest<any>('get', `/projects/${currentProject.id}/dev/status`);
      setIsRunning(response.running);
      setPreviewUrl(response.url || null);

      // Auto-start if not running
      if (!response.running) {
        await startDevServer();
      }
    } catch (error) {
      console.error('Failed to check dev server status:', error);
      // Try to start anyway
      await startDevServer();
    }
  };

  const startDevServer = async () => {
    if (!currentProject || isStarting) return;

    setIsStarting(true);
    try {
      const response = await apiRequest<any>('post', `/projects/${currentProject.id}/dev/start`);
      setIsRunning(true);
      setPreviewUrl(response.url);
    } catch (error: any) {
      console.error('Failed to start dev server:', error);
      // Don't show alert for auto-start failures, just log them
    } finally {
      setIsStarting(false);
    }
  };

  const refreshPreview = () => {
    const iframe = document.getElementById('preview-iframe') as HTMLIFrameElement;
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Preview Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <h2 className="font-semibold">Live Preview</h2>

        <div className="flex items-center gap-2">
          {isRunning && (
            <>
              <button
                onClick={refreshPreview}
                className="p-1.5 hover:bg-muted rounded-md transition-colors"
                title="Refresh preview"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 hover:bg-muted rounded-md transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 bg-muted/30 relative">
        {showAIOverlay && currentProject && isRunning && previewUrl && (
          <AIPromptOverlay
            projectId={currentProject.id}
            onDismiss={() => setShowAIOverlay(false)}
            onStartChat={() => {
              // TODO: Focus chat input when chat component is available
              setShowAIOverlay(false);
            }}
          />
        )}
        {!isRunning ? (
          <div className="flex items-center justify-center h-full text-center">
            <div className="max-w-md w-full px-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              {installProgress ? (
                <>
                  <p className="text-muted-foreground mb-4">{installProgress.message}</p>
                  {installProgress.progress > 0 && (
                    <div className="w-full bg-muted rounded-full h-2.5 mb-2">
                      <div 
                        className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${installProgress.progress}%` }}
                      ></div>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground/70">{installProgress.stage}</p>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Opening your project...
                </p>
              )}
            </div>
          </div>
        ) : previewUrl ? (
          <iframe
            id="preview-iframe"
            src={previewUrl}
            className="w-full h-full border-0"
            title="Live Preview"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Loading preview...</p>
          </div>
        )}
      </div>
    </div>
  );
}
